<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCatalogProducts } from 'dashboard/composables/useCatalogProducts';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const {
  products,
  isLoading,
  catalogId,
  hasMore,
  error,
  fetchProducts,
  loadMore,
} = useCatalogProducts();

const query = ref('');
const selectedProducts = ref([]);
const isMultiSelect = ref(false);

let searchTimeout = null;

watch(query, newQuery => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    fetchProducts(props.inboxId, newQuery);
  }, 300);
});

onMounted(() => {
  fetchProducts(props.inboxId);
});

const toggleProduct = product => {
  if (isMultiSelect.value) {
    const idx = selectedProducts.value.findIndex(
      p => p.retailer_id === product.retailer_id
    );
    if (idx >= 0) {
      selectedProducts.value.splice(idx, 1);
    } else if (selectedProducts.value.length < 30) {
      selectedProducts.value.push(product);
    }
  } else {
    selectedProducts.value = [product];
    emit('onSelect', [product], catalogId.value);
  }
};

const isSelected = product => {
  return selectedProducts.value.some(
    p => p.retailer_id === product.retailer_id
  );
};

const confirmMultiSelect = () => {
  if (selectedProducts.value.length > 0) {
    emit('onSelect', selectedProducts.value, catalogId.value);
  }
};

const toggleMode = () => {
  isMultiSelect.value = !isMultiSelect.value;
  selectedProducts.value = [];
};

const selectCatalogMessage = () => {
  emit('onSelect', [], catalogId.value, 'catalog_message');
};

const formatPrice = product => {
  if (!product.price) return '';
  return product.price;
};
</script>

<template>
  <div class="w-full">
    <!-- Search + mode toggle -->
    <div class="flex gap-2 mb-3">
      <div
        class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
      >
        <Icon icon="i-lucide-search" class="text-n-slate-11 size-4" />
        <input
          v-model="query"
          type="search"
          :placeholder="t('CATALOG_PRODUCTS.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
      </div>
      <button
        class="flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-medium transition-colors"
        :class="
          isMultiSelect
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6'
        "
        @click="toggleMode"
      >
        <Icon
          :icon="
            isMultiSelect ? 'i-lucide-layout-grid' : 'i-lucide-square-check'
          "
          class="size-3.5"
        />
        {{
          isMultiSelect
            ? t('CATALOG_PRODUCTS.SINGLE_PRODUCT')
            : t('CATALOG_PRODUCTS.MULTI_PRODUCT')
        }}
      </button>
      <Button
        v-if="catalogId"
        outline
        slate
        sm
        icon="i-lucide-store"
        :label="t('CATALOG_PRODUCTS.SEND_CATALOG')"
        @click="selectCatalogMessage"
      />
    </div>

    <!-- Error state -->
    <div
      v-if="error"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <Icon icon="i-lucide-shopping-bag" class="size-10 text-n-slate-10 mb-3" />
      <p class="text-sm text-n-slate-11">
        {{ t('CATALOG_PRODUCTS.NO_CATALOG') }}
      </p>
    </div>

    <!-- Products grid -->
    <div
      v-else
      class="bg-n-background outline-n-container outline outline-1 rounded-lg max-h-[24rem] overflow-y-auto p-2.5"
    >
      <!-- Loading -->
      <div
        v-if="isLoading && !products.length"
        class="flex justify-center py-8"
      >
        <span class="i-lucide-loader-2 animate-spin size-6 text-n-slate-10" />
      </div>

      <!-- Product cards -->
      <div v-else-if="products.length" class="grid grid-cols-2 gap-2">
        <button
          v-for="product in products"
          :key="product.retailer_id || product.id"
          class="flex gap-3 p-2.5 rounded-lg text-left transition-colors border"
          :class="
            isSelected(product)
              ? 'border-n-brand bg-n-brand/5'
              : 'border-transparent hover:bg-n-alpha-2 dark:hover:bg-n-solid-2'
          "
          @click="toggleProduct(product)"
        >
          <div
            class="shrink-0 w-14 h-14 rounded-lg bg-n-slate-3 overflow-hidden flex items-center justify-center"
          >
            <img
              v-if="product.image_url"
              :src="product.image_url"
              :alt="product.name"
              class="w-full h-full object-cover"
            />
            <Icon
              v-else
              icon="i-lucide-package"
              class="size-6 text-n-slate-10"
            />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ product.name }}
            </p>
            <p
              v-if="formatPrice(product)"
              class="text-xs text-n-slate-11 mt-0.5"
            >
              {{ formatPrice(product) }}
            </p>
            <span
              v-if="product.availability"
              class="inline-block mt-1 px-1.5 py-0.5 text-[10px] rounded font-medium"
              :class="
                product.availability === 'in stock'
                  ? 'bg-n-green-3 text-n-green-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              {{
                product.availability === 'in stock'
                  ? t('CATALOG_PRODUCTS.IN_STOCK')
                  : t('CATALOG_PRODUCTS.OUT_OF_STOCK')
              }}
            </span>
          </div>
          <div v-if="isMultiSelect" class="shrink-0 flex items-center">
            <span
              class="size-5 rounded border flex items-center justify-center"
              :class="
                isSelected(product)
                  ? 'bg-n-brand border-n-brand'
                  : 'border-n-slate-7'
              "
            >
              <Icon
                v-if="isSelected(product)"
                icon="i-lucide-check"
                class="size-3 text-white"
              />
            </span>
          </div>
        </button>
      </div>

      <!-- Empty state -->
      <div v-else class="py-8 text-center">
        <p class="text-sm text-n-slate-11">
          {{ t('CATALOG_PRODUCTS.NO_PRODUCTS') }}
        </p>
      </div>

      <!-- Load more -->
      <div v-if="hasMore" class="flex justify-center mt-3">
        <button
          class="px-4 py-2 text-xs font-medium text-n-brand hover:underline disabled:opacity-50"
          :disabled="isLoading"
          @click="loadMore(inboxId)"
        >
          <span
            v-if="isLoading"
            class="i-lucide-loader-2 animate-spin size-3 mr-1 inline-block"
          />
          {{ t('CATALOG_PRODUCTS.LOAD_MORE') }}
        </button>
      </div>
    </div>

    <!-- Multi-select confirm bar -->
    <div
      v-if="isMultiSelect && selectedProducts.length > 0"
      class="flex items-center justify-between mt-3 px-3 py-2 rounded-lg bg-n-brand/10"
    >
      <span class="text-sm font-medium text-n-brand">
        {{ t('CATALOG_PRODUCTS.SELECTED', { count: selectedProducts.length }) }}
        <span
          v-if="selectedProducts.length >= 30"
          class="text-xs text-n-slate-11 ml-1"
        >
          {{ t('CATALOG_PRODUCTS.MAX_PRODUCTS') }}
        </span>
      </span>
      <button
        class="px-4 py-1.5 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand/90"
        @click="confirmMultiSelect"
      >
        {{ t('CATALOG_PRODUCTS.SEND') }}
      </button>
    </div>
  </div>
</template>
